import {
    removeCapacitySuffixIfNeeded,
    getAdjustedCapacityString,
} from './utils/getAdjustedStringUtils';
import { updateCapacityValue } from '../../mutators/capabilityFilterMutators';
import getStore from '../../store/store';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import { observer } from 'mobx-react-lite';
import { SpinButton } from '@fluentui/react/lib/SpinButton';
import loc from 'owa-localize';
import * as React from 'react';
import {
    capacityDecreaseAriaLabel,
    capacityIncreaseAriaLabel,
    emptySpinButtonLabel,
    capacityFilterLabel,
} from './CapabilityFilters.locstring.json';
import { useModeStyles } from 'owa-react-hooks/lib/useModeStyles';

export interface CapacityFilterProps {
    maxCapacity: number;
    onChange?: () => void;
}

const styles = {
    base: require('./CapabilityFilters.scss'),
    desktop: require('./CapabilityFilters.outlookdesktop.scss'),
    mac: {
        ...require('./CapabilityFilters.macoutlook.scss'),
        spinButtonWrapperFocused: {
            border: '4px solid !important',
        },
    },
};

export default observer(function CapacityFilter(props: CapacityFilterProps) {
    const { getClass, getStyle } = useClientStyles(styles);
    const { getModeAwareClass } = useModeStyles(getClass);
    const getCapacityValue = () => {
        const { selectedCapacity } = getStore().roomFilterState;
        const { maxCapacity } = props;
        // need to validate selectedCapacity if it has value, otherwise return "Any" as default
        return selectedCapacity
            ? getAdjustedCapacityString(selectedCapacity, maxCapacity)
            : loc(emptySpinButtonLabel);
    };
    const onCapacityIncrement = (capacityValue: string) => {
        if (!capacityValue || capacityValue === loc(emptySpinButtonLabel)) {
            capacityValue = String(0);
        }
        const updatedCapacity = addToCapacity(capacityValue, 1);
        onChange(updatedCapacity);
        return getCapacityValue();
    };
    const onCapacityDecrement = (capacityValue: string) => {
        const updatedCapacity = addToCapacity(capacityValue, -1);
        onChange(updatedCapacity);
        return getCapacityValue();
    };
    const addToCapacity = (stringCapacity: string, valueToAdd: number) => {
        const capacity = Number(removeCapacitySuffixIfNeeded(stringCapacity));
        return getAdjustedCapacityString(String(capacity + valueToAdd), props.maxCapacity);
    };
    const onCapacityValidate = (capacity: string) => {
        const adjustedCapacity = getAdjustedCapacityString(capacity, props.maxCapacity);
        onChange(adjustedCapacity);
        return getCapacityValue();
    };
    const onChange = (capacity: string) => {
        updateCapacityValue(capacity);
        if (props.onChange) {
            props.onChange();
        }
    };

    return (
        <SpinButton
            value={getCapacityValue()}
            disabled={!props.maxCapacity}
            styles={{
                // Passing style object directly for spinButtonWrapperFocused as Fabric SpinButton doesn't support mergeStyles for this prop
                // https://github.com/microsoft/fluentui/issues/13955
                spinButtonWrapperFocused: getStyle('spinButtonWrapperFocused'),
                spinButtonWrapper: getModeAwareClass('spinButtonWrapper'),
                input: getModeAwareClass('spinButtonInput'),
            }}
            min={0}
            max={props.maxCapacity}
            ariaValueNow={props.maxCapacity ? parseInt(getCapacityValue()) : null}
            onValidate={onCapacityValidate}
            onIncrement={onCapacityIncrement}
            onDecrement={onCapacityDecrement}
            incrementButtonAriaLabel={loc(capacityIncreaseAriaLabel)}
            decrementButtonAriaLabel={loc(capacityDecreaseAriaLabel)}
            inputProps={{
                'aria-label': loc(capacityFilterLabel),
                'aria-valuenow': parseInt(getCapacityValue()),
            }}
        />
    );
});
